/**
 * Custom hook for NCR Edit state management
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  qualityAPI,
  platformSettingsAPI,
  coreAPI,
  fetchAllPages,
  NonConformanceReport,
  NCRDocument,
  NCRComment,
  NCRActionLog,
  UserProfile,
  SourceOfRejection,
  CommonisedCorrectiveAction,
  FaultCodeGroup,
  FaultCodeCategory,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { displayPrice } from '../../lib/unitPrice';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { NCREditFormData, ProgressStep } from './types';
import { RCA_REQUIRED_FIELDS, RCA_FIELD_LABELS, CAR_REQUIRED_FIELDS, CAR_FIELD_LABELS } from './constants';

export function useNCREdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // CR-4707 precision, same source the create form and PO Line pop-up read.
  const { preferences } = useFormatters();
  const priceDecimals = preferences.unit_price_decimals;

  // Data states
  const [ncr, setNcr] = useState<NonConformanceReport | null>(null);
  const [documents, setDocuments] = useState<NCRDocument[]>([]);
  const [comments, setComments] = useState<NCRComment[]>([]);
  const [actionLogs, setActionLogs] = useState<NCRActionLog[]>([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [customerUsers, setCustomerUsers] = useState<UserProfile[]>([]);

  // Classification dropdown options
  const [sourcesOfRejection, setSourcesOfRejection] = useState<SourceOfRejection[]>([]);
  const [commonisedActions, setCommonisedActions] = useState<CommonisedCorrectiveAction[]>([]);
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [faultCategories, setFaultCategories] = useState<FaultCodeCategory[]>([]);

  // Form states
  const [formData, setFormData] = useState<NCREditFormData>({});
  const [newComment, setNewComment] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  // CR-6024: customer-side CAR rejection modal (Reject & Rework path).
  const [rejectCARDialogOpen, setRejectCARDialogOpen] = useState(false);
  // CR-6024 r3: customer-side reopen-closed-NCR modal.
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  // Supplier rejects the whole NCR with a free-text reason; customer then
  // approves (voids the NCR) or declines. The audit log + void happen backend-side.
  const [rejectNCRDialogOpen, setRejectNCRDialogOpen] = useState(false);
  // 8D scoring: RC/CA scores (1-5) the customer grades the submitted CAR
  // with before clicking Approve / Reject & Rework. Sent with the decision.
  const [rcScore, setRcScore] = useState<number | null>(null);
  const [caScore, setCaScore] = useState<number | null>(null);
  // CR-5999: per-field validation errors surfaced inline next to each
  // required RCA field on Submit RCA. Keys are field names from
  // RCA_REQUIRED_FIELDS; presence of a key = the field failed validation.
  const [rcaFieldErrors, setRcaFieldErrors] = useState<Record<string, string>>({});
  // CR-6000: per-field validation errors surfaced inline next to each required
  // CAR field on Submit CAR. Keys are field names from CAR_REQUIRED_FIELDS;
  // presence of a key = the field failed validation. Mirrors rcaFieldErrors.
  const [carFieldErrors, setCarFieldErrors] = useState<Record<string, string>>({});

  // Template type: 1 = Mandatory 8D (default), 2 = Optional 8D
  // Fetched from platform settings
  const [ncrTemplate, setNcrTemplate] = useState<1 | 2>(1);

  // Template 2 configuration - prefer formData (live edits) over saved ncr data
  const is8DRequired = (formData.is_8d_required as boolean | undefined) ?? ncr?.is_8d_required ?? true;
  const isCollectionRequired = (formData.is_collection_required as boolean | undefined) ?? ncr?.is_collection_required ?? false;
  const isCreditRequired = (formData.is_credit_required as boolean | undefined) ?? ncr?.is_credit_required ?? false;
  const liabilityType = (formData.liability_type as number | undefined) ?? ncr?.liability_type ?? 0;

  // Get user role from auth context
  const { user } = useAuth();
  const userRole = user?.company_type || user?.platform?.toLowerCase() || 'customer';
  const isCustomer = userRole === 'customer' || userRole === 'helix';
  const isSupplier = userRole === 'supplier';
  // CR-6024 r4: separate gate for write actions that require ACTUAL customer
  // affiliation. Helix tenants cannot directly fire customer/supplier
  // workflow transitions (the backend's user_can_act_for_company check
  // rejects them); they have to use Login As first. Surfacing the Approve /
  // Reject / Reopen buttons to a Helix admin produces a confusing "Only the
  // company that raised the NCR can reject the CAR" error toast on click.
  // Keep `isCustomer` permissive for read-only UI (banners, panels), but
  // gate the action affordances on this stricter flag.
  const canActAsCustomer = userRole === 'customer';

  // Workflow permissions. CR-5980: supplier edits the RCA tab when the NCR
  // is in the 'created' state (customer has submitted, supplier hasn't yet),
  // and edits the CAR tab once at 'ca_submitted'.
  const canEditStep1 = isCustomer && ncr?.status === 'draft';
  // When the customer returns the CAR for rework the NCR drops to
  // 'ca_submitted' with a car_rejection_reason set. In that state the supplier
  // must be able to amend the RCA too — not just the CAR — so re-enable RCA
  // editing on a rework, in addition to the initial 'created' submission.
  // For an internal NCR the customer's assigned team owns the 8D; otherwise
  // the supplier does. The backend enforces the same in submit_rca/submit_car.
  const isInternalNcr = !!ncr?.is_internal;
  const owns8D = isInternalNcr ? isCustomer : isSupplier;
  const carReturnedForRework =
    ncr?.status === 'ca_submitted' && !!(ncr?.car_rejection_reason || '').trim();
  const canEditStep2 = owns8D && (ncr?.status === 'created' || carReturnedForRework);
  const canEditStep3 = owns8D && ncr?.status === 'ca_submitted';
  // The supplier may INPUT the Corrective Action Report alongside the RCA
  // (status 'created'), not only after the RCA is submitted. The Save button
  // persists it as a draft; submitting the CAR still requires 'ca_submitted'
  // (canEditStep3), so the RCA must be submitted first.
  const canInputCAR = owns8D && (ncr?.status === 'created' || ncr?.status === 'ca_submitted');

  // Generate Template 2 progress steps
  const getTemplate2Steps = (): ProgressStep[] => {
    const liabilityDetermined = liabilityType > 0;
    const liabilityLabel = liabilityType === 1 ? 'Supplier Liability' :
      liabilityType === 2 ? 'Customer Liability' :
      liabilityType === 3 ? 'Shared Liability' :
      liabilityType === 4 ? 'No Liability' : 'Liability';

    const steps: ProgressStep[] = [
      {
        title: 'Created',
        icon: '📋',
        state: currentStep >= 1 ? 'completed' : 'pending',
      },
      {
        title: liabilityDetermined ? liabilityLabel : 'Liability',
        icon: liabilityType === 1 ? '🏭' : liabilityType === 2 ? '🏢' : '⚖️',
        state: liabilityDetermined ? 'completed' : 'pending',
      },
    ];

    if (is8DRequired) {
      steps.push({
        title: '8D Completed',
        icon: '⚠️',
        state: currentStep >= 3 ? 'completed' : 'pending',
      });
    }

    if (isCollectionRequired) {
      const collectionDone = ncr?.collection_completed || ncr?.collection_process?.status === 'completed';
      steps.push({
        title: 'Collection',
        icon: '📦',
        state: collectionDone ? 'completed' : 'pending',
      });
    }

    if (isCreditRequired) {
      const creditDone = ncr?.credit_completed || ncr?.credit_process?.status === 'processed';
      steps.push({
        title: 'Credit',
        icon: '💰',
        state: creditDone ? 'completed' : 'pending',
      });
    }

    steps.push({
      title: 'Closed',
      icon: '✅',
      state: currentStep >= 4 ? 'completed' : 'pending',
    });

    return steps;
  };

  // Fetch platform settings to get NCR template preference
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        const response = await platformSettingsAPI.getSettings();
        const template = response.data.ncr_template;
        if (template === 1 || template === 2) {
          setNcrTemplate(template);
        }
      } catch (error) {
        console.error('Error fetching platform settings:', error);
        // Default to template 1 if fetch fails
      }
    };
    const fetchClassificationOptions = async () => {
      try {
        // Walk pages — the backend clamps page_size, so one request silently truncated.
        const [sors, fgs, fcs, ccas] = await Promise.all([
          fetchAllPages<SourceOfRejection>((page) => qualityAPI.getSourcesOfRejection({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeGroup>((page) => qualityAPI.getFaultGroups({ is_active: true, page_size: 100, page })),
          fetchAllPages<FaultCodeCategory>((page) => qualityAPI.getFaultCategories({ is_active: true, page_size: 100, page })),
          fetchAllPages<CommonisedCorrectiveAction>((page) => qualityAPI.getCommonisedCorrectiveActions({ is_active: true, page_size: 100, page })),
        ]);
        setSourcesOfRejection(sors);
        setFaultGroups(fgs);
        setFaultCategories(fcs);
        setCommonisedActions(ccas);
      } catch {
        console.error('Error fetching classification options');
      }
    };

    fetchPlatformSettings();
    fetchClassificationOptions();
  }, []);

  // Fetch NCR details
  useEffect(() => {
    if (id) {
      fetchNCRDetails();
    }
  }, [id]);

  // CR-6001: ``isRefresh`` distinguishes the initial mount fetch (which
  // toggles ``loading`` to show the full-screen spinner) from post-save /
  // post-action refetches (which keep ``loading`` false so the Tabs tree
  // stays mounted). Without this split, every save would unmount the
  // inner Root Cause / Corrective Action Tabs, and the inner Tabs'
  // ``defaultValue`` (status-driven) would snap the user back to Root
  // Cause — which is exactly the "page refreshes back to RCA" symptom
  // CR-6001 reported.
  const fetchNCRDetails = async (isRefresh = false) => {
    if (!id) return;

    try {
      if (!isRefresh) setLoading(true);
      const [ncrResponse, docsResponse, commentsResponse, logsResponse] = await Promise.all([
        qualityAPI.getNCR(parseInt(id)),
        qualityAPI.getNCRDocuments(parseInt(id)),
        qualityAPI.getNCRComments(parseInt(id)),
        qualityAPI.getNCRActionLogs(parseInt(id)),
      ]);

      const ncrData = ncrResponse.data;
      setNcr(ncrData);
      // The column holds 4 decimals so the Regional Settings precision can go
      // that high, but the API returns the stored scale verbatim — "10.0000"
      // for a price entered as 10. `displayPrice` trims that back to the
      // configured default while keeping any extra decimal the user actually
      // entered, so re-saving an untouched NCR cannot quietly round its price.
      // Done once on load, not per render, which would rewrite the box while
      // it is being typed into.
      setFormData({
        ...(ncrData as NCREditFormData),
        unit_price: ncrData.unit_price != null && ncrData.unit_price !== ''
          ? displayPrice(ncrData.unit_price, priceDecimals)
          : '',
      });
      // 8D scoring: seed the review dropdowns with any previously-saved
      // grades (e.g. scores captured on an earlier rework round).
      setRcScore(ncrData.rc_score ?? null);
      setCaScore(ncrData.ca_score ?? null);
      setDocuments(docsResponse.data.results);
      setComments(commentsResponse.data.results);
      setActionLogs(logsResponse.data.results);

      // Fetch customer site users for the buyer dropdown. Customer-side
      // only: the endpoint is scoped to the customer's own company and 403s
      // for a supplier, who cannot edit the buyer anyway — calling it
      // logged a console error on every supplier load and action.
      if (ncrData.company && !isSupplier) {
        try {
          const usersResponse = await coreAPI.getCompanyUsers(ncrData.company);
          setCustomerUsers(usersResponse.data);
        } catch {
          console.error('Error fetching customer users');
        }
      }

      // Determine current step based on status. CR-5980: 'draft' and
      // 'created' are both pre-supplier-action so they map to step 1; the
      // stepper only advances once the supplier submits CA or CAR.
      if (ncrData.status === 'draft') setCurrentStep(1);
      else if (ncrData.status === 'created') setCurrentStep(1);
      else if (ncrData.status === 'ca_submitted') setCurrentStep(2);
      else if (ncrData.status === 'car_submitted') setCurrentStep(3);
      else if (ncrData.status === 'closed') setCurrentStep(4);
    } catch (error) {
      console.error('Error fetching NCR details:', error);
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    try {
      setSaving(true);
      // Build patch payload from editable fields
      const payload: Record<string, unknown> = {};
      // CR-6001: Save is shared by the Detail / RCA / CAR tabs, so the
      // payload has to carry the union of all three workflow steps'
      // fields. Previously only Detail-tab fields were listed, so the
      // CAR (and RCA) Save button fired an empty PATCH — the toast
      // said "Saved successfully" but the textareas re-mounted with
      // the unchanged server-side state and the user's edits vanished.
      const editableFields = [
        // Step 1 — Detail
        'grn_number', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_internal', 'assigned_to',
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        // is_replacement_required was missing from this whitelist, so the
        // Detail tab's "Replacement Required = Yes" never reached the API
        // and silently reverted to No on reload.
        'is_replacement_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
        // CR-7069: 8D D1 Team Formation fields
        'team_leader', 'team_members', 'team_formation_date',
        // Step 2 — Root Cause Analysis (draft persistence; submit_rca still
        // owns the status transition created → ca_submitted)
        'disposition_instructions', 'immediate_corrective_action',
        'why_1', 'why_2', 'why_3', 'why_4', 'why_5', 'selected_root_cause',
        // Step 3 — Corrective Action Report (draft persistence; submit_car
        // still owns the status transition ca_submitted → car_submitted)
        'corrective_action', 'preventive_action', 'impact_assessment',
        'similar_products_affected', 'impact_on_similar_products',
        'training_required', 'training_details',
        'doc_process_flow_updated', 'doc_control_plan_updated',
        'doc_fmea_updated', 'doc_work_instructions_updated',
        'doc_other_updated',
        'documentation_references', 'corrective_action_notes',
        'action_completion_date',
        // Customer review 2026-07: 8D extensions + containment status
        'commonised_corrective_action', 'horizontal_deployment_parts',
        'containment_required', 'containment_current_stock',
        'containment_qty_ok', 'containment_qty_not_ok',
        'containment_confirmed',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      toast.success('Saved successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error saving NCR:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitDetail = async () => {
    if (!id) return;

    try {
      setSaving(true);
      // Save all form data first before transitioning status
      const payload: Record<string, unknown> = {};
      const editableFields = [
        'grn_number', 'po_number', 'part_number', 'description',
        'batch_number', 'works_order', 'reject_qty', 'batch_qty',
        'unit_price', 'shortage_qty', 'severity', 'defect_description',
        'buyer_name', 'liability_type', 'liability_classification',
        'is_internal', 'assigned_to',
        'is_8d_required', 'is_collection_required', 'is_credit_required',
        // is_replacement_required was missing from this whitelist, so the
        // Detail tab's "Replacement Required = Yes" never reached the API
        // and silently reverted to No on reload.
        'is_replacement_required',
        'proposed_rejection_type', 'disposition_quantity', 'action_required',
        'source_of_rejection_option', 'fault_group', 'fault_category',
        // Customer review 2026-07: containment status lives on the Detail tab
        'containment_required', 'containment_current_stock',
        'containment_qty_ok', 'containment_qty_not_ok',
        'containment_confirmed',
      ];
      for (const field of editableFields) {
        if (formData[field] !== undefined) {
          payload[field] = formData[field];
        }
      }
      await qualityAPI.updateNCR(parseInt(id), payload as Partial<NonConformanceReport>);
      await qualityAPI.submitDetail(parseInt(id));
      toast.success('Detail submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting detail:', error);
      toast.error('Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRCA = async () => {
    if (!id || !formData) return;

    // CR-5999: Field-level validation messaging. Build a Record of
    // missing fields, set it so the RCA tab renders inline red borders
    // and error text below each empty required textarea, and show a
    // toast summary using user-friendly labels (not raw API field
    // names like "why_1").
    const missingErrors: Record<string, string> = {};
    for (const field of RCA_REQUIRED_FIELDS) {
      const value = formData[field as keyof typeof formData];
      if (typeof value !== 'string' || !value.trim()) {
        missingErrors[field] = 'This field is required';
      }
    }

    if (Object.keys(missingErrors).length > 0) {
      setRcaFieldErrors(missingErrors);
      const friendlyLabels = Object.keys(missingErrors).map(
        (k) => RCA_FIELD_LABELS[k] || k
      );
      toast.error(
        `Please complete the following required fields: ${friendlyLabels.join(', ')}`
      );
      return;
    }

    // All fields populated — clear any prior error state.
    setRcaFieldErrors({});

    try {
      setSaving(true);
      await qualityAPI.submitRCA(parseInt(id), {
        why_1: formData.why_1 || '',
        why_2: formData.why_2 || '',
        why_3: formData.why_3 || '',
        why_4: formData.why_4 || '',
        why_5: formData.why_5 || '',
        selected_root_cause: formData.selected_root_cause || '',
        immediate_corrective_action: formData.immediate_corrective_action || '',
        disposition_instructions: formData.disposition_instructions,
      });
      toast.success('Root Cause Analysis submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting RCA:', error);
      toast.error('Failed to submit RCA');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCAR = async () => {
    if (!id || !formData) return;

    // CR-6000: Field-level validation messaging. Build a Record of missing
    // fields, set it so the CAR tab renders inline red borders and error text
    // below each empty required textarea, and show a toast summary using
    // user-friendly labels (not raw API names like "preventive_action").
    // Mirrors the CR-5999 RCA pattern so the two supplier-facing submit flows
    // behave identically — and crucially gives a persistent on-screen surface
    // rather than a toast-only message that users can miss ("nothing happens").
    const missingErrors: Record<string, string> = {};
    for (const field of CAR_REQUIRED_FIELDS) {
      const value = formData[field as keyof typeof formData];
      if (typeof value !== 'string' || !value.trim()) {
        missingErrors[field] = 'This field is required';
      }
    }

    if (Object.keys(missingErrors).length > 0) {
      setCarFieldErrors(missingErrors);
      const friendlyLabels = Object.keys(missingErrors).map(
        (k) => CAR_FIELD_LABELS[k] || k
      );
      toast.error(
        `Please complete the following required fields: ${friendlyLabels.join(', ')}`
      );
      return;
    }

    // All fields populated — clear any prior error state.
    setCarFieldErrors({});

    try {
      setSaving(true);
      await qualityAPI.submitCAR(parseInt(id), {
        corrective_action: formData.corrective_action || '',
        preventive_action: formData.preventive_action || '',
        impact_assessment: formData.impact_assessment || '',
        impact_on_similar_products: formData.impact_on_similar_products || '',
        similar_products_affected: formData.similar_products_affected || false,
        training_required: formData.training_required || false,
        training_details: formData.training_details,
        doc_process_flow_updated: formData.doc_process_flow_updated || false,
        doc_control_plan_updated: formData.doc_control_plan_updated || false,
        doc_fmea_updated: formData.doc_fmea_updated || false,
        doc_work_instructions_updated: formData.doc_work_instructions_updated || false,
        doc_other_updated: formData.doc_other_updated || '',
        documentation_references: formData.documentation_references || '',
        corrective_action_notes: formData.corrective_action_notes || '',
        // Customer review 2026-07: 8D extensions
        commonised_corrective_action: (formData.commonised_corrective_action as number | null) ?? null,
        horizontal_deployment_parts: (formData.horizontal_deployment_parts as string) || '',
      });
      toast.success('Corrective Action Report submitted successfully');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting CAR:', error);
      toast.error('Failed to submit CAR');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!id || !newComment.trim()) return;

    try {
      await qualityAPI.addNCRComment(parseInt(id), newComment, false);
      setNewComment('');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleClose = async () => {
    if (!id) return;

    try {
      // 8D scoring: send the customer's CAR grades with the approve decision.
      await qualityAPI.closeNCR(parseInt(id), {
        ...(rcScore != null ? { rc_score: rcScore } : {}),
        ...(caScore != null ? { ca_score: caScore } : {}),
      });
      toast.success('NCR approved and closed');
      setCloseDialogOpen(false);
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      // CR-6024 r2: surface the backend's actual error (permission, state
      // gate, missing collection/credit) so the user sees WHY Approve didn't
      // close the NCR. Previously this just said "Failed to close NCR" which
      // looked like the button was broken (Jacq rework).
      console.error('Error closing NCR:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      const backendMsg = err.response?.data?.error || err.response?.data?.detail;
      const msg = backendMsg
        || (err.response?.status === 403
              ? 'You do not have permission to close this NCR. If you are a Helix admin, use Login As to act as a customer user.'
              : 'Failed to close NCR');
      toast.error(msg);
    }
  };

  // CR-6024: customer rejects the submitted CAR and sends it back to the
  // supplier for rework. The reason becomes the banner the supplier sees
  // on the Corrective Action tab when the NCR drops back to ca_submitted.
  const handleRejectCAR = async (rejectionReason: string): Promise<boolean> => {
    if (!id) return false;
    const reason = rejectionReason.trim();
    if (!reason) {
      toast.error('Please provide a reason before sending the CAR back for rework.');
      return false;
    }
    try {
      // 8D scoring: send the customer's CAR grades with the rework decision.
      await qualityAPI.rejectCAR(parseInt(id), reason, {
        ...(rcScore != null ? { rc_score: rcScore } : {}),
        ...(caScore != null ? { ca_score: caScore } : {}),
      });
      toast.success('CAR rejected. Supplier notified to rework.');
      setRejectCARDialogOpen(false);
      await fetchNCRDetails(true);
      return true;
    } catch (error: unknown) {
      // CR-6024 r2: same surfacing as handleClose — the previous generic toast
      // hid the underlying permission/state-gate failure (Jacq rework).
      console.error('Error rejecting CAR:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      const backendMsg = err.response?.data?.error || err.response?.data?.detail;
      const msg = backendMsg
        || (err.response?.status === 403
              ? 'You do not have permission to reject this CAR. If you are a Helix admin, use Login As to act as a customer user.'
              : 'Failed to reject CAR');
      toast.error(msg);
      return false;
    }
  };

  // Persist a single RC/CA grade the moment it's picked — independent of the
  // approve/reject decision — so the register column and 8D Score (= RC*CA)
  // update immediately. Previously the scores only saved as a side-effect of
  // close/reject, so a customer who graded but did neither lost them (the
  // "entered but not showing / not calculating" bug).
  const persistCarScore = async (field: 'rc_score' | 'ca_score', value: number) => {
    if (field === 'rc_score') setRcScore(value); else setCaScore(value);
    if (!id) return;
    try {
      await qualityAPI.setCarScores(parseInt(id), { [field]: value });
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to save score');
    }
  };
  const handleRcScoreChange = (value: number) => persistCarScore('rc_score', value);
  const handleCaScoreChange = (value: number) => persistCarScore('ca_score', value);

  // CR-6024 r3: customer reopens a closed NCR. Spec acceptance criterion
  // "Closed NCRs can be reopened by customer users if required." Backend
  // /reopen_ncr/ already exists and transitions closed -> ca_submitted.
  const handleReopen = async () => {
    if (!id) return;
    try {
      await qualityAPI.reopenNCR(parseInt(id));
      toast.success('NCR reopened');
      setReopenDialogOpen(false);
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      console.error('Error reopening NCR:', error);
      const err = error as { response?: { status?: number; data?: { error?: string; detail?: string } } };
      const backendMsg = err.response?.data?.error || err.response?.data?.detail;
      const msg = backendMsg
        || (err.response?.status === 403
              ? 'You do not have permission to reopen this NCR. If you are a Helix admin, use Login As to act as a customer user.'
              : 'Failed to reopen NCR');
      toast.error(msg);
    }
  };

  // Supplier: request rejection of the submitted NCR with a free-text reason.
  // Returns false so the dialog stays open on failure.
  const handleRejectNCR = async (reason: string): Promise<boolean> => {
    if (!id) return false;
    try {
      await qualityAPI.rejectNCR(parseInt(id), reason);
      toast.success('NCR rejection requested — awaiting customer decision');
      setRejectNCRDialogOpen(false);
      await fetchNCRDetails(true);
      return true;
    } catch (error: unknown) {
      console.error('Error rejecting NCR:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to reject NCR');
      return false;
    }
  };

  // Customer: approve the supplier's rejection — the backend voids the NCR
  // (status → deleted) and writes the audit trail, so navigate back to the list.
  const handleApproveRejection = async () => {
    if (!id) return;
    try {
      await qualityAPI.approveRejection(parseInt(id));
      toast.success('NCR rejection approved — NCR voided');
      navigate('/quality');
    } catch (error: unknown) {
      console.error('Error approving NCR rejection:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to approve rejection');
    }
  };

  // Customer: decline the supplier's rejection — the NCR continues unchanged.
  const handleDeclineRejection = async () => {
    if (!id) return;
    try {
      await qualityAPI.declineRejection(parseInt(id));
      toast.success('NCR rejection declined');
      await fetchNCRDetails(true);
    } catch (error: unknown) {
      console.error('Error declining NCR rejection:', error);
      const err = error as { response?: { data?: { error?: string; detail?: string } } };
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Failed to decline rejection');
    }
  };

  const handleConfirmCollection = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCollection(parseInt(id));
      toast.success('Collection confirmed');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error confirming collection:', error);
      toast.error('Failed to confirm collection');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCredit = async () => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.confirmCredit(parseInt(id));
      toast.success('Credit confirmed');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error confirming credit:', error);
      toast.error('Failed to confirm credit');
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Collection Process handlers
  const handleInitiateCollection = async (data: {
    proposed_collection_date?: string;
    collection_address?: string;
    collection_notes?: string;
    customer_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCollection(parseInt(id), data);
      toast.success('Collection process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating collection:', error);
      // CR-5538: surface the backend's actual message ("Only the company
      // that raised the NCR can initiate collection", "Collection is not
      // required for this NCR", etc.) instead of swallowing it as a
      // generic "Failed to initiate collection" toast.
      toast.error(getApiErrorMessage(error, 'Failed to initiate collection'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCollectionStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateCollectionStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCollectionStatus>[1]);
      toast.success(`Collection ${newStatus === 'completed' ? 'completed' : 'updated'}`);
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error updating collection status:', error);
      // CR-6073 rework: this handler was the last of the collection/credit/
      // replacement update-status actions still swallowing the backend's
      // reason ("Cannot transition collection from 'X' to 'Y'", "Only the
      // company that raised the NCR can complete collection", etc.) behind
      // a generic toast. Mirrors the getApiErrorMessage pattern already
      // used by handleUpdateCreditStatus / handleUpdateReplacementStatus.
      toast.error(getApiErrorMessage(error, 'Failed to update collection'));
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Credit Process handlers
  const handleInitiateCredit = async (data: {
    credit_amount?: number;
    credit_currency?: string;
    credit_reason?: string;
    customer_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateCredit(parseInt(id), data);
      toast.success('Credit process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating credit:', error);
      // CR-5539: surface the backend's actual message instead of generic
      // "Failed to initiate credit". Same pattern as CR-5538.
      toast.error(getApiErrorMessage(error, 'Failed to initiate credit'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitCreditNote = async (data: FormData) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.submitCreditNote(parseInt(id), data);
      toast.success('Credit note submitted');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting credit note:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit credit note'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCreditStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateCreditStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateCreditStatus>[1]);
      const labels: Record<string, string> = {
        approved: 'Credit approved and closed',
        rejected: 'Credit rejected',
      };
      toast.success(labels[newStatus] || 'Credit updated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error updating credit status:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update credit'));
    } finally {
      setSaving(false);
    }
  };

  // Multi-step Replacement Process handlers (mirror the credit handlers)
  const handleInitiateReplacement = async (data: {
    replacement_qty?: number;
    replacement_reason?: string;
    customer_notes?: string;
  }) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.initiateReplacement(parseInt(id), data);
      toast.success('Replacement process initiated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error initiating replacement:', error);
      toast.error(getApiErrorMessage(error, 'Failed to initiate replacement'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReplacementDispatch = async (data: FormData) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.submitReplacementDispatch(parseInt(id), data);
      toast.success('Dispatch details submitted');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error submitting despatch details:', error);
      toast.error(getApiErrorMessage(error, 'Failed to submit dispatch details'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateReplacementStatus = async (newStatus: string, data?: Record<string, unknown>) => {
    if (!id) return;
    try {
      setSaving(true);
      await qualityAPI.updateReplacementStatus(parseInt(id), {
        status: newStatus,
        ...data,
      } as Parameters<typeof qualityAPI.updateReplacementStatus>[1]);
      const labels: Record<string, string> = {
        approved: 'Replacement receipt confirmed and closed',
        rejected: 'Replacement rejected',
      };
      toast.success(labels[newStatus] || 'Replacement updated');
      await fetchNCRDetails(true);
    } catch (error) {
      console.error('Error updating replacement status:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update replacement'));
    } finally {
      setSaving(false);
    }
  };

  // DE review (Vaida, 2026-07): the 8D is its own workflow and must be
  // approvable irrespective of the collection / credit / replacement claim
  // workflows, which run independently. Approval therefore gates only on the
  // CAR having been submitted, not on those commercial steps completing.
  const canClose = !!ncr && ncr.status === 'car_submitted';

  const updateFormData = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // CR-5999: clear the inline RCA error for this field as soon as the
    // user starts typing — the error has been addressed.
    if (rcaFieldErrors[field]) {
      setRcaFieldErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
    // CR-6000: same self-clearing behaviour for the CAR field errors.
    if (carFieldErrors[field]) {
      setCarFieldErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const goBack = () => navigate('/quality');

  return {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    currentStep,
    ncrTemplate,
    newComment,
    closeDialogOpen,

    // Permissions
    isCustomer,
    canActAsCustomer,
    isSupplier,
    canEditStep1,
    canEditStep2,
    canEditStep3,
    canInputCAR,

    // CR-5999: per-field RCA validation errors (e.g. {'why_1': 'This field
    // is required'}) that the RCA tab renders inline next to each empty
    // required field on a failed Submit RCA attempt.
    rcaFieldErrors,

    // CR-6000: per-field CAR validation errors (e.g. {'preventive_action':
    // 'This field is required'}) rendered inline on a failed Submit CAR.
    carFieldErrors,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    // CR-6024
    rejectCARDialogOpen,
    setRejectCARDialogOpen,
    handleRejectCAR,
    // CR-6024 r3: reopen-closed-NCR modal
    reopenDialogOpen,
    setReopenDialogOpen,
    handleReopen,
    // Supplier reject-NCR + customer approve/decline
    rejectNCRDialogOpen,
    setRejectNCRDialogOpen,
    handleRejectNCR,
    handleApproveRejection,
    handleDeclineRejection,
    // 8D scoring: customer CAR grades captured with the approve/rework decision
    rcScore,
    caScore,
    setRcScore,
    setCaScore,
    handleRcScoreChange,
    handleCaScoreChange,
    updateFormData,

    // Customer users for buyer dropdown
    customerUsers,

    // Classification options
    sourcesOfRejection,
    commonisedActions,
    faultGroups,
    faultCategories,

    // Template 2 config
    liabilityType,
    is8DRequired,
    isCollectionRequired,
    isCreditRequired,

    // Actions
    // CR-6001: external refreshes (e.g. after document upload) go through
    // refresh mode so the Tabs tree doesn't unmount and snap the user back
    // to the Root Cause sub-tab.
    refreshData: () => fetchNCRDetails(true),
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,
    handleConfirmCollection,
    handleConfirmCredit,
    getTemplate2Steps,

    // Multi-step workflow actions
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleInitiateCredit,
    handleSubmitCreditNote,
    handleUpdateCreditStatus,
    handleInitiateReplacement,
    handleSubmitReplacementDispatch,
    handleUpdateReplacementStatus,

    // Computed
    canClose,
  };
}
